import { Box, DialogContent, Grid, TextField, Typography } from "@mui/material"
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons"
import { Dispatch, SetStateAction, useCallback, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { makeStyles } from "tss-react/mui"
import { useFormikContext } from "formik"
import { CreateOrEditSurveyFormValues, SurveyQuestion, TopicItemForMerchant, TopicType } from "../merchantSurveyTypes"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { DialogStateProps } from "./CreateUpdateSurveyFormContent"
import { SurveyTopicsSelector } from "./SurveyTopicsSelector"
import { StandardDialog } from "../../../../../shared/components/dialogs/StandardDialog"

const useStyles = makeStyles()((theme) => ({
    dialogContent: {
        padding: "0.3rem 2.5rem 2rem 2.5rem",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
    },
    dialogBox: {
        gap: "3rem",
    },
    listWrap: {
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
    },
    listTitle: {
        color: theme.palette.tailwind.slate[900],
        background: theme.palette.tailwind.slate[50],
        padding: "0.875rem 0.75rem",
    },
    infoWrap: {
        display: "grid",
        gridTemplateColumns: "14px 1fr",
        background: theme.palette.tailwind.sky[50],
        borderRadius: "0.5rem",
        gap: "0.625rem",
        padding: "0.75rem",
        color: theme.palette.tailwind.sky[700],
    },
    button: {
        fontSize: "0.875rem",
        margin: "3rem auto 0",
        padding: "0.5rem 2rem",
        borderRadius: "0.5rem",
    },
}))

type CreateUpdateQuestionDialogProps = Readonly<{
    dialogState: DialogStateProps
    setDialogState: Dispatch<SetStateAction<DialogStateProps>>
}>

export const CreateUpdateQuestionDialog = ({ dialogState, setDialogState }: CreateUpdateQuestionDialogProps) => {
    const { classes } = useStyles()
    const { values, setFieldValue } = useFormikContext<CreateOrEditSurveyFormValues>()

    const [list, setList] = useState<SurveyQuestion>(() => {
        return (
            dialogState.item || {
                id: 0,
                title: "",
                [TopicType.GOOD_TOPICS]: [],
                [TopicType.BAD_TOPICS]: [],
            }
        )
    })

    const handleClose = useCallback(() => {
        setDialogState((prev) => ({ ...prev, item: null, show: false, index: null }))
    }, [setDialogState])

    const handleSelectionChange = useCallback((selectedTopics: TopicItemForMerchant[], topicType: TopicType) => {
        setList((prev) => ({ ...prev, [topicType]: selectedTopics }))
    }, [])

    const handleSave = () => {
        const updatedQuestions = [...values.questions]

        if (dialogState.item) {
            const questionIndex = updatedQuestions.findIndex((question) => question.id === dialogState.item?.id)
            updatedQuestions[questionIndex] = list
        } else {
            updatedQuestions.push({ ...list, id: updatedQuestions.length + 1 })
        }

        setFieldValue("questions", updatedQuestions)
        handleClose()
    }

    return (
        <StandardDialog
            dialogTitle={
                <LocalizedStrict id="create-or-edit-merchant-survey-dialog-title">
                    <span>Add question</span>
                </LocalizedStrict>
            }
            open={dialogState.show}
            onClose={handleClose}
            maxWidthProp="sm"
            scroll="body"
        >
            <DialogContent className={classes.dialogContent}>
                <Grid container direction="column" className={classes.dialogBox}>
                    <LocalizedStrict id="create-or-edit-merchant-survey-dialog-field-question" attrs={{ label: true }}>
                        <TextField
                            type="text"
                            value={list.title}
                            onChange={(event) => {
                                const { value } = event.target
                                setList((prevList) => ({ ...prevList, title: value }))
                            }}
                            label="Question"
                            fullWidth
                        />
                    </LocalizedStrict>
                    <Grid className={classes.listWrap}>
                        <LocalizedStrict id="create-or-edit-merchant-survey-dialog-topics-title">
                            <Typography variant="subtitle1" className={classes.listTitle}>
                                Select topics:
                            </Typography>
                        </LocalizedStrict>
                        <SurveyTopicsSelector
                            topicType={TopicType.GOOD_TOPICS}
                            initialSelectedTopics={list.goodTopics}
                            onSelectionChange={handleSelectionChange}
                        />
                        <SurveyTopicsSelector
                            topicType={TopicType.BAD_TOPICS}
                            initialSelectedTopics={list.badTopics}
                            onSelectionChange={handleSelectionChange}
                        />

                        <Box className={classes.infoWrap}>
                            <FontAwesomeIcon icon={faCircleInfo} />
                            <LocalizedStrict id="create-or-edit-merchant-survey-dialog-topics-info">
                                <Typography variant="body2">
                                    Good topics can be used by participant to select what they found particularly good.
                                    Bad topics can be used to specify what they would like to see improved.
                                </Typography>
                            </LocalizedStrict>
                        </Box>
                    </Grid>
                </Grid>

                <LocalizedStrict id="create-or-edit-merchant-survey-dialog-save-button">
                    <StandardButton
                        fullWidth={false}
                        onClick={handleSave}
                        updateDefaultClasses={classes.button}
                        disabled={
                            !(
                                !!list.title &&
                                !!list[TopicType.GOOD_TOPICS].length &&
                                !!list[TopicType.BAD_TOPICS].length
                            )
                        }
                    >
                        Save
                    </StandardButton>
                </LocalizedStrict>
            </DialogContent>
        </StandardDialog>
    )
}
